import { hasAccess } from "@/components/common/has-access";
import { usePermissionContext } from "@/components/common/permission-context";
import { useQuery } from "@tanstack/react-query";
import {
  getEditorPage,
  getHandbookFeedback,
  getHandbookHelp,
  getHandbookOwners,
  getHandbookPage,
  getHandbookSearchIndex,
  getHandbookSummary,
  getHandbookTree,
  getManageTree,
  getPageRevisions,
} from "../services/handbook-api";
import type { FeedbackStatus } from "../types";

export const HANDBOOK_AREA = "handbook";

/**
 * Handbook content changes rarely and every reader asks for it, so reads are
 * cached for a few minutes and not refetched on every tab focus.
 */
const READ_CACHE = { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false } as const;

export const handbookKeys = {
  all: ["handbook"] as const,
  summary: ["handbook", "summary"] as const,
  tree: ["handbook", "tree"] as const,
  page: (slug: string) => ["handbook", "page", slug] as const,
  searchIndex: ["handbook", "search-index"] as const,
  help: (area: string) => ["handbook", "help", area] as const,
  manageTree: ["handbook", "manage", "tree"] as const,
  editorPage: (id: string) => ["handbook", "manage", "page", id] as const,
  revisions: (id: string) => ["handbook", "manage", "revisions", id] as const,
  owners: ["handbook", "manage", "owners"] as const,
  feedback: (status?: FeedbackStatus) => ["handbook", "manage", "feedback", status ?? "all"] as const,
};

/**
 * Who may read and who may write the Handbook — the same rule the server
 * applies (View / Edit on the handbook area plus the verb). Deliberately not
 * useIsSuperAdmin: editing is a grid grant, so delegating it later needs no
 * code change.
 */
export function useHandbookAccess() {
  const ctx = usePermissionContext();
  return {
    isLoaded: !!ctx.isLoaded,
    canRead: hasAccess(ctx, "read", HANDBOOK_AREA),
    canEdit: hasAccess(ctx, "write", HANDBOOK_AREA),
  };
}

export function useHandbookSummary(enabled = true) {
  return useQuery({
    queryKey: handbookKeys.summary,
    queryFn: getHandbookSummary,
    enabled,
    ...READ_CACHE,
  });
}

export function useHandbookTree() {
  return useQuery({ queryKey: handbookKeys.tree, queryFn: getHandbookTree, ...READ_CACHE });
}

export function useHandbookPage(slug: string | undefined) {
  return useQuery({
    queryKey: handbookKeys.page(slug ?? ""),
    queryFn: () => getHandbookPage(slug!),
    enabled: !!slug,
    retry: (count, error: any) => error?.response?.status !== 404 && count < 2,
    ...READ_CACHE,
  });
}

export function useHandbookSearchIndex(enabled: boolean) {
  return useQuery({
    queryKey: handbookKeys.searchIndex,
    queryFn: getHandbookSearchIndex,
    enabled,
    ...READ_CACHE,
  });
}

export function useHandbookHelp(area: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: handbookKeys.help(area ?? ""),
    queryFn: () => getHandbookHelp(area!),
    enabled: enabled && !!area,
    ...READ_CACHE,
  });
}

// ── editors ──

export function useManageTree(enabled = true) {
  return useQuery({
    queryKey: handbookKeys.manageTree,
    queryFn: getManageTree,
    enabled,
    refetchOnWindowFocus: false,
  });
}

/**
 * The working copy. Never refetched behind the editor's back: a focus refetch
 * must not reset text someone is typing. The editor initialises once per
 * (page, version) and only a deliberate reload replaces it.
 */
export function useEditorPage(id: string | undefined) {
  return useQuery({
    queryKey: handbookKeys.editorPage(id ?? ""),
    queryFn: () => getEditorPage(id!),
    enabled: !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function usePageRevisions(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: handbookKeys.revisions(id ?? ""),
    queryFn: () => getPageRevisions(id!),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
  });
}

export function useHandbookOwners(enabled: boolean) {
  return useQuery({
    queryKey: handbookKeys.owners,
    queryFn: getHandbookOwners,
    enabled,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useHandbookFeedback(status: FeedbackStatus | undefined, enabled: boolean) {
  return useQuery({
    queryKey: handbookKeys.feedback(status),
    queryFn: () => getHandbookFeedback(status),
    enabled,
    refetchOnWindowFocus: false,
  });
}
